/** @jsxImportSource @emotion/react */
import React from 'react';
import { Card, Tag, Typography } from 'antd';
import { css } from '@emotion/react';

const { Title, Text } = Typography;

type Tone = 'cream' | 'lilac' | 'pink' | 'mint' | 'sky';

const toneStyles: Record<Tone, { bg: string; border: string; glow: string }> = {
  cream: { bg: '#FFF8E7', border: '#F3E2B4', glow: 'rgba(255, 214, 120, 0.35)' },
  lilac: { bg: '#F5EFF8', border: '#E2D2EA', glow: 'rgba(186, 150, 214, 0.30)' },
  pink: { bg: '#FDEFEC', border: '#F3CFC5', glow: 'rgba(233, 150, 122, 0.28)' },
  mint: { bg: '#EDF7EC', border: '#CFE6CB', glow: 'rgba(140, 196, 128, 0.30)' },
  sky: { bg: '#EDF4FA', border: '#CCDFEE', glow: 'rgba(120, 170, 214, 0.28)' },
};

const styles = {
  card: (tone: Tone) =>
    css({
      height: '100%',
      overflow: 'hidden',
      background: toneStyles[tone].bg,
      border: `1px solid ${toneStyles[tone].border}`,
      cursor: 'pointer',
      transition: 'transform 140ms ease, box-shadow 140ms ease',
      '&:hover': {
        transform: 'translateY(-3px)',
        boxShadow: '0 10px 22px rgba(90, 70, 30, 0.10)',
      },
      // Растим иллюстрацию на ховере — карточки живые, а не кликабельные плитки.
      '&:hover img': { transform: 'scale(1.05) rotate(-1deg)' },
    }),
  cardDisabled: css({
    cursor: 'default',
    '&:hover': { transform: 'none', boxShadow: 'none' },
    '&:hover img': { transform: 'none' },
  }),
  body: css({
    padding: 18,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    minHeight: 236,
  }),
  art: (tone: Tone) =>
    css({
      alignSelf: 'center',
      width: 132,
      height: 132,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      // Тёплое пятно под иллюстрацией вместо жёсткой плашки-иконки.
      background: `radial-gradient(circle at 50% 55%, ${toneStyles[tone].glow} 0%, rgba(255,255,255,0) 68%)`,
    }),
  artImg: css({
    maxWidth: '100%',
    maxHeight: '100%',
    display: 'block',
    transition: 'transform 220ms ease',
    filter: 'drop-shadow(0 4px 8px rgba(80, 60, 20, 0.14))',
  }),
  artFaded: css({ opacity: 0.88 }),
  title: css({ marginBottom: '0 !important', textAlign: 'center' }),
  description: css({ textAlign: 'center' }),
  spacer: css({ flex: 1 }),
  badgeRow: css({ display: 'flex', justifyContent: 'center' }),
};

type HomeMiniCardProps = {
  title: string;
  description: string;
  /** Иллюстрация карточки (src). Заменяет прежний глиф-бейдж. */
  art?: string;
  tone?: Tone;
  badge?: string;
  onClick?: () => void;
  disabled?: boolean;
};

const HomeMiniCard: React.FC<HomeMiniCardProps> = ({
  title,
  description,
  art,
  tone = 'cream',
  badge,
  onClick,
  disabled,
}) => {
  const handleClick = () => {
    if (disabled) return;
    onClick?.();
  };

  return (
    <Card
      css={[styles.card(tone), disabled ? styles.cardDisabled : undefined]}
      styles={{ body: { padding: 0 } }}
      variant="borderless"
      onClick={handleClick}
    >
      <div css={styles.body}>
        {art ? (
          <span css={styles.art(tone)}>
            <img
              src={art}
              alt=""
              css={[styles.artImg, disabled ? styles.artFaded : undefined]}
              loading="lazy"
            />
          </span>
        ) : null}
        <Title level={4} css={styles.title}>
          {title}
        </Title>
        <Text type="secondary" css={styles.description}>
          {description}
        </Text>
        <div css={styles.spacer} />
        {badge ? (
          <span css={styles.badgeRow}>
            <Tag color={disabled ? 'default' : 'green'} style={{ marginInlineEnd: 0 }}>
              {badge}
            </Tag>
          </span>
        ) : null}
      </div>
    </Card>
  );
};

export default HomeMiniCard;
